import React, { FC } from "react";

import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useMount } from "react-use";

import { PaginatedList } from "@/components/templates";
import { Document, DocumentsRelatedPageParams, DocumentsRelatedPageState } from "@/shared/domain";
import { useNavigation, useRouterHook } from "@/shared/hooks";
import * as tagManager from "@/tagManager";
import { useUserFeedback } from "@/userFeedback";

import DocumentTable from "../components/DocumentTable/DocumentTable";
import useDocumentQueries from "../useDocumentQueries";

const DocumentsRelatedPage: FC = () => {
    const {
        state: { title },
    } = useRouterHook<DocumentsRelatedPageState>();
    const { filter, id } = useParams<DocumentsRelatedPageParams>();
    const { t } = useTranslation();
    const { navToRelatedDocumentsSearch } = useNavigation();

    const { tableMode, useDocumentsRelatedPageQuery } = useDocumentQueries();

    const {
        page,
        setPage,
        isFetching,
        totalOfPages,
        result: documents,
        handlePageSizeChange,
        orderBy,
        handleSort,
    } = useDocumentsRelatedPageQuery(id, filter);

    const { incUsageCount } = useUserFeedback();

    const navToSearchPage = () => {
        navToRelatedDocumentsSearch(id, title, filter);
    };

    useMount(() => {
        incUsageCount();
        tagManager.pushViewItemListEvent("document", "Documents");
    });

    const renderTable = (items: Document[]) => (
        <DocumentTable documents={items} orderBy={orderBy} handleSort={handleSort} />
    );

    return (
        <PaginatedList<Document>
            page={page}
            emptyListMessage={t("Documents.Empty")}
            pageTitle={t("PageTitles.MyDocuments")}
            renderTable={renderTable}
            isFetchingList={isFetching}
            isLoading={isFetching}
            setPage={setPage}
            totalOfPages={totalOfPages}
            items={documents}
            handlePageSizeChange={handlePageSizeChange}
            openSearch={totalOfPages > 0 && navToSearchPage}
            tableMode={tableMode}
            hideGlobalFilters
        />
    );
};

export default DocumentsRelatedPage;
